import React, { useEffect, useState } from 'react';
import memberstackDOM from '@memberstack/dom';
import './App.css';

import ZeroDteWssClient, { requestIndexHistoryRefresh } from './Components/0_DTE_Websocket_Client.js';
import ZeroDteGreeksWebsocketClient from './Components/0_DTE_Greeks_Websocket_Client';
import MarketIntelligenceWebsocketClient from './Components/Clients/MarketIntelligenceWebsocketClient';
import DashboardLayout from './Components/Dashboard/DashboardLayout';
import { PARTIAL_REPLAY_WIDGET_IDS } from './Components/Dashboard/widgetRegistry';
import { startStream } from './Services/streamClient';
import {
  chainSnapshotWssUrl,
  getActiveChainSnapshotClient,
  hasValidChainSnapshotCredentials,
  startChainSnapshotClient,
} from './Services/chainSnapshotClient';
import { chainSnapshotMinuteEnd, useOptionsStore } from './Store/optionsStore';
import {
  etDateKey,
  isMarketOpen,
  millisecondsUntilNextMarketOpen,
  mostRecentSessionDate,
} from './Utils/marketHours';
import { hasCurrentTradingDayLiveData, hydrateDailyCache, persistDailyCache } from './Store/dailyCache';
import { readWindowNameChainSnaps, writeWindowNameChainSnaps } from './Utils/chainSnapshotBridge';
import { windowedSnap } from './Utils/strikeFlow';
import { countReplayCoverageGaps, countReplayGaps } from './Utils/replayGaps';
import { REPLAY_DATE_CACHE_CAPACITY, validateReplayV2Payload } from './Utils/replayV2';
import usePlaybackStore from './Store/playbackStore';
import useWebSocketDataStore from './Store/WebSocketDataStore';
import useDashboardConfig from './Store/dashboardConfigStore';
import useMarketIntelligenceStore from './Store/marketIntelligenceStore';
import {
  loadReplayDatasetSession,
  persistReplayDatasetSession,
  replayCoverageMissingRange,
  replayDatasetSessionHasCompatibleProvenance,
  replayDatasetSessionMetadataHasCompatibleProvenance,
  replayDatasetSessionMatchesCoverage,
  touchReplayHistoryDate,
} from './Store/playbackHistoryStore';

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE;
const MEMBERSTACK_PUBLIC_KEY = process.env.REACT_APP_MEMBERSTACK_PUBLIC_KEY;
export const REPLAY_DISCOVERY_FALLBACK_MS = 2_000;
export const REPLAY_DISCOVERY_RETRY_MS = 2_000;
export const REPLAY_DISCOVERY_RETRY_MAX_MS = 60_000;

export function scheduleReplayDiscoveryFallback(markReady, isCancelled = () => false) {
  return setTimeout(() => {
    if (!isCancelled()) markReady(true);
  }, REPLAY_DISCOVERY_FALLBACK_MS);
}

export function replayDiscoveryRetryDelay(attempt = 0) {
  return Math.min(
    REPLAY_DISCOVERY_RETRY_MS * (2 ** Math.max(0, attempt)),
    REPLAY_DISCOVERY_RETRY_MAX_MS,
  );
}

export function scheduleReplayDiscoveryRetry(
  refresh,
  { attempt = 0, isCancelled = () => false } = {},
) {
  return setTimeout(() => {
    if (!isCancelled()) refresh({ force: true });
  }, replayDiscoveryRetryDelay(attempt));
}

export function shouldLockDocumentScroll(isDataReady, playbackMode) {
  return !isDataReady && playbackMode !== 'playing';
}

const allowedPlanIds = [
  // SPX Gamma plans that should grant access to this dashboard.
  'pln_free-trial-k2e50rfn',
  'pln_legacy-membership-337d0n3j',
  'pln_spx-gamma-7-day-free-trial-ny3f01hl',
  'pln_vip-membership-c0ea0lhm',
  'pln_premium-seller-membership-q2en0as4',
  'pln_7-day-membership-zd1d088k',
  'pln_0-dte-active-trader-81r0g74'
];

const REDIRECTS = {
  login: 'https://www.spxgamma.com/log-in',
  join: 'https://www.spxgamma.com/pricing',
  denied: 'https://www.spxgamma.com/membership/denied',
};

function normalizeStatus(status) {
  return String(status || '').trim().toLowerCase();
}

function isValidStatus(status) {
  const normalized = normalizeStatus(status);
  return normalized === 'active' || normalized === 'trialing';
}

function hasValidCredentials(credentials) {
  return Boolean(
    credentials?.memberId &&
      credentials?.planId &&
      credentials?.status
  );
}

function getValidPlan(member) {
  if (!member?.planConnections || member.planConnections.length === 0) {
    return null;
  }

  return member.planConnections.find((connection) => {
    const priceId = connection.payment?.priceId;
    const planId = connection.planId;

    const isAllowed =
      allowedPlanIds.includes(priceId) || allowedPlanIds.includes(planId);

    return isAllowed && isValidStatus(connection.status);
  });
}

function buildManualMemberCredentials() {
  const credentials = {
    memberId: process.env.REACT_APP_LOCAL_MEMBER_ID,
    status: normalizeStatus(process.env.REACT_APP_LOCAL_PLAN_STATUS || 'active'),
    planId: process.env.REACT_APP_LOCAL_PLAN_ID,
  };

  if (!hasValidCredentials(credentials)) {
    throw new Error(
      'Missing local member credentials. Set REACT_APP_LOCAL_MEMBER_ID, REACT_APP_LOCAL_PLAN_ID, and REACT_APP_LOCAL_PLAN_STATUS in .env.local.'
    );
  }

  return credentials;
}

async function buildMemberstackCredentials() {
  if (!MEMBERSTACK_PUBLIC_KEY) {
    throw new Error('Missing REACT_APP_MEMBERSTACK_PUBLIC_KEY for Memberstack auth mode.');
  }

  const memberstack = memberstackDOM.init({
    publicKey: MEMBERSTACK_PUBLIC_KEY,
  });

  const { data: member } = await memberstack.getCurrentMember();

  if (!member) {
    window.location.href = REDIRECTS.login;
    return null;
  }

  const validPlan = getValidPlan(member);

  if (!validPlan) {
    const hasPlans =
      member.planConnections && member.planConnections.length > 0;

    window.location.href = hasPlans ? REDIRECTS.denied : REDIRECTS.join;
    return null;
  }

  const validId = allowedPlanIds.includes(validPlan.payment?.priceId)
    ? validPlan.payment.priceId
    : validPlan.planId;

  const credentials = {
    memberId: member.id,
    status: normalizeStatus(validPlan.status),
    planId: validId,
  };

  if (!hasValidCredentials(credentials)) {
    throw new Error('Unable to build valid Memberstack credentials.');
  }

  return credentials;
}

async function resolveMemberCredentials() {
  if (AUTH_MODE === 'memberstack') {
    return buildMemberstackCredentials();
  }

  if (AUTH_MODE === 'manual') {
    return buildManualMemberCredentials();
  }

  throw new Error(
    `Invalid REACT_APP_AUTH_MODE: ${AUTH_MODE || 'not configured'}`
  );
}

function tradingDateEt(value = Date.now()) {
  return new Date(value).toLocaleDateString('en-CA', {
    timeZone: 'America/New_York',
  });
}

const chainSnapsKey = (tradingDate = mostRecentSessionDate()) => `chainSnaps_${tradingDate}`;

function isTradingDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || '');
}

function chainSnapsTradingDate(snaps = []) {
  for (let index = snaps.length - 1; index >= 0; index -= 1) {
    if (Number.isFinite(snaps[index]?.t)) return tradingDateEt(snaps[index].t);
  }
  return tradingDateEt();
}

function readChainSnapsCache(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const snaps = JSON.parse(raw);
    if (Array.isArray(snaps)) return snaps;
  } catch {
    // Remove only the malformed entry; another cache source may still be valid.
  }
  try {
    localStorage.removeItem(key);
  } catch {
    // Non-fatal: private mode or restricted storage.
  }
  return null;
}

export function snapshotsForTradingDate(snaps = [], tradingDate = tradingDateEt()) {
  if (!Array.isArray(snaps)) return [];
  return snaps.filter((snapshot) => (
    Number.isFinite(snapshot?.t) && tradingDateEt(snapshot.t) === tradingDate
  ));
}

export function mergeChainSnapshotsAtMinute(apiSnap = {}, currentSnap = {}) {
  const cleanApiSnap = stripHydrationFlagsAndStampSource(apiSnap);
  const cleanCurrentSnap = stripHydrationFlagsAndStampSource(currentSnap);
  const byStrike = { ...(cleanApiSnap.byStrike || {}) };
  for (const [strike, cell] of Object.entries(cleanCurrentSnap.byStrike || {})) {
    byStrike[strike] = {
      ...(byStrike[strike] || {}),
      ...(cell || {}),
    };
  }
  const currentSpot = Number.isFinite(cleanCurrentSnap.spot) ? cleanCurrentSnap.spot : null;
  const currentByStrike = cleanCurrentSnap.byStrike || {};
  const serviceAddsData = Object.entries(cleanApiSnap.byStrike || {}).some(([strike, cell]) => {
    const currentCell = currentByStrike[strike];
    return !currentCell || Object.keys(cell || {}).some((key) => (
      !Object.prototype.hasOwnProperty.call(currentCell, key)
    ));
  }) || ['hydrationFrom', 'hydrationTo'].some((field) => (
    Number.isFinite(cleanApiSnap[field]) && !Number.isFinite(cleanCurrentSnap[field])
  )) || (currentSpot == null && Number.isFinite(cleanApiSnap.spot));
  const sourceTimes = [cleanApiSnap.sourceT, cleanCurrentSnap.sourceT].filter(Number.isFinite);
  return {
    ...cleanApiSnap,
    ...cleanCurrentSnap,
    spot: currentSpot ?? (cleanApiSnap.spot ?? null),
    byStrike,
    sourceT: serviceAddsData || !Number.isFinite(cleanCurrentSnap.sourceT)
      ? (sourceTimes.length ? Math.max(...sourceTimes) : null)
      : cleanCurrentSnap.sourceT,
    serviceSnapshotOverlay: serviceAddsData || Boolean(cleanCurrentSnap.serviceSnapshotOverlay),
  };
}

function hasValidLegSplit(cell) {
  if (cell?.hasLegSplit !== true) return true;
  return ['cnv', 'pnv', 'cvol', 'pvol'].every((field) => Number.isFinite(Number(cell[field])));
}

function hasValidChainSnapshotShape(snapshot) {
  if (!snapshot || !Number.isFinite(Number(snapshot.t)) || !snapshot.byStrike || typeof snapshot.byStrike !== 'object') {
    return false;
  }
  return Object.values(snapshot.byStrike).every((cell) => cell && typeof cell === 'object' && hasValidLegSplit(cell));
}

export function mergeBackfilledChainSnapshots(
  apiSnaps = [],
  currentSnaps = [],
  { markSourceTransitions = false } = {},
) {
  const byTimestamp = new Map();
  const sourceByTimestamp = new Map();
  const retainedSeamTimestamps = new Set();
  let latestApiTimestamp = null;

  for (const rawSnapshot of apiSnaps) {
    const snapshot = stripHydrationFlagsAndStampSource(rawSnapshot);
    byTimestamp.set(snapshot.t, snapshot);
    sourceByTimestamp.set(snapshot.t, 'api');
    if (Number.isFinite(snapshot?.t)) {
      latestApiTimestamp = latestApiTimestamp == null
        ? snapshot.t
        : Math.max(latestApiTimestamp, snapshot.t);
    }
  }

  for (const rawSnapshot of currentSnaps) {
    const snapshot = stripHydrationFlagsAndStampSource(rawSnapshot);
    if (rawSnapshot?.hydrationSeam === true) {
      retainedSeamTimestamps.add(snapshot.t);
    }
    const existing = byTimestamp.get(snapshot.t);
    if (existing) {
      byTimestamp.set(
        snapshot.t,
        mergeChainSnapshotsAtMinute(existing, snapshot)
      );
      sourceByTimestamp.set(snapshot.t, 'current');
      continue;
    }

    byTimestamp.set(snapshot.t, snapshot);
    sourceByTimestamp.set(snapshot.t, 'current');
  }

  const merged = [...byTimestamp.values()]
    .sort((left, right) => left.t - right.t)
    .slice(-400);
  let hasCurrentAfterApi = false;
  for (let index = 0; index < merged.length; index += 1) {
    const snapshot = merged[index];
    const source = sourceByTimestamp.get(snapshot.t);
    if (source === 'current' && snapshot.t > latestApiTimestamp) {
      hasCurrentAfterApi = true;
    }
    const crossesSourceBoundary = (
      markSourceTransitions
      && index > 0
      && source !== sourceByTimestamp.get(merged[index - 1].t)
    );
    if (index > 0 && (crossesSourceBoundary || retainedSeamTimestamps.has(snapshot.t))) {
      merged[index] = { ...snapshot, hydrationSeam: true };
    }
  }
  if (!hasCurrentAfterApi && Number.isFinite(latestApiTimestamp)) {
    const tailIndex = merged.findIndex(
      (snapshot) => snapshot?.t === latestApiTimestamp
    );
    if (tailIndex >= 0) {
      merged[tailIndex] = {
        ...merged[tailIndex],
        hydrationBoundaryTail: true,
      };
    }
  }
  return merged;
}

// Shape validation for an INIT or UPDATE payload. The transport is trusted only
// to be authenticated; the payload is still checked before it reaches the store,
// exactly as the REST body was.
export function validateChainSnapshotPayload(payload, { allowEmpty = false } = {}) {
  // A transport-level failure already knows why (disconnected, timeout,
  // not_authenticated); do not flatten it into invalid_response.
  if (payload?.ok === false) return { ok: false, reason: payload.reason || 'unavailable' };

  const tradingDate = payload?.tradingDate;
  const snaps = payload?.snaps;
  if (
    !isTradingDate(tradingDate)
    || !Array.isArray(snaps)
    || !snaps.every(hasValidChainSnapshotShape)
    || snapshotsForTradingDate(snaps, tradingDate).length !== snaps.length
  ) {
    return { ok: false, reason: 'invalid_response' };
  }
  if (!snaps.length && !allowEmpty) return { ok: false, reason: 'empty' };

  return {
    ok: true,
    tradingDate,
    snaps,
    index: Object.prototype.hasOwnProperty.call(payload || {}, 'index')
      ? validateHydratedIndexPayload(payload.index, tradingDate)
      : null,
  };
}

export function validateHydratedIndexPayload(payload, requiredTradingDate) {
  if (payload?.status !== 'available') {
    return {
      status: 'unavailable',
      reason: payload?.reason || 'unavailable',
      tradingDate: payload?.tradingDate || null,
      symbol: 'SPX',
      bars: [],
      coverage: payload?.coverage || null,
    };
  }
  const bars = payload?.bars;
  const valid = (
    payload?.tradingDate === requiredTradingDate
    && String(payload?.symbol || '').replace('I:', '') === 'SPX'
    && Array.isArray(bars)
    && bars.length > 0
    && bars.every((bar) => (
      Number.isFinite(Number(bar?.time))
      && tradingDateEt(Number(bar.time)) === requiredTradingDate
      && Number.isFinite(Number(bar?.open))
      && Number.isFinite(Number(bar?.high))
      && Number.isFinite(Number(bar?.low))
      && Number.isFinite(Number(bar?.close))
      && Math.min(
        Number(bar.open),
        Number(bar.high),
        Number(bar.low),
        Number(bar.close)
      ) > 0
      && Number(bar.high) >= Math.max(Number(bar.open), Number(bar.close))
      && Number(bar.low) <= Math.min(Number(bar.open), Number(bar.close))
    ))
  );
  if (!valid) {
    return {
      status: 'unavailable',
      reason: payload?.tradingDate !== requiredTradingDate ? 'session_mismatch' : 'invalid_response',
      tradingDate: payload?.tradingDate || null,
      symbol: 'SPX',
      bars: [],
      coverage: payload?.coverage || null,
    };
  }
  return { ...payload, symbol: 'SPX' };
}

function hasValidReplayFrameShape(frame) {
  return Boolean(
    frame
    && Number.isFinite(Number(frame.t))
    && Number.isFinite(Number(frame.spot))
    && frame.rowsBySym
    && typeof frame.rowsBySym === 'object'
    && Object.keys(frame.rowsBySym).length > 0
  );
}

export function validateReplayFramePayload(payload) {
  if (payload?.ok === false) return { ok: false, reason: payload.reason || 'unavailable' };

  const tradingDate = payload?.tradingDate;
  const frames = payload?.frames;
  if (
    !isTradingDate(tradingDate)
    || !Array.isArray(frames)
    || !frames.every(hasValidReplayFrameShape)
  ) {
    return { ok: false, reason: 'invalid_response' };
  }

  return {
    ok: true,
    tradingDate,
    frames,
    index: Object.prototype.hasOwnProperty.call(payload || {}, 'index')
      ? validateHydratedIndexPayload(payload.index, tradingDate)
      : null,
  };
}

export function validateReplayPayload(payload) {
  return payload?.schemaVersion === 2
    ? validateReplayV2Payload(payload)
    : validateReplayFramePayload(payload);
}

let activeReplayHistoryPreload = null;
let activeReplayHistoryTopUp = null;
const REPLAY_BUSY_RETRY_DELAYS_MS = [50, 100, 200, 400, 800];
const LIVE_MI_HISTORY_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 30_000, 60_000];
const replayRequestQueueByClient = new WeakMap();

function replayCancellationResult(remainingGaps = null) {
  return {
    ok: false,
    status: 'cancelled',
    reason: 'cancelled',
    remainingGaps,
  };
}

async function requestReplayWithCancellation(client, options, signal = null) {
  if (signal?.aborted) return { ok: false, reason: 'cancelled' };
  const cancelRequest = () => client.cancelReplay?.('superseded');
  signal?.addEventListener('abort', cancelRequest, { once: true });
  try {
    const result = await client.requestReplay(options);
    return signal?.aborted ? { ok: false, reason: 'cancelled' } : result;
  } finally {
    signal?.removeEventListener('abort', cancelRequest);
  }
}

function queueReplayRequest(client, request, { signal = null } = {}) {
  const previous = replayRequestQueueByClient.get(client) || Promise.resolve();
  let removeAbortListener = () => {};
  const cancellation = signal ? new Promise((resolve) => {
    const cancel = () => resolve({ ok: false, reason: 'cancelled' });
    if (signal.aborted) {
      cancel();
      return;
    }
    signal.addEventListener('abort', cancel, { once: true });
    removeAbortListener = () => signal.removeEventListener('abort', cancel);
  }) : null;
  const requestTurn = previous.catch(() => undefined).then(() => (
    signal?.aborted ? { ok: false, reason: 'cancelled' } : request()
  ));
  const queued = cancellation ? Promise.race([requestTurn, cancellation]) : requestTurn;
  const tail = requestTurn.then(() => undefined, () => undefined).finally(() => {
    removeAbortListener();
    if (replayRequestQueueByClient.get(client) === tail) {
      replayRequestQueueByClient.delete(client);
    }
  });
  replayRequestQueueByClient.set(client, tail);
  return queued;
}

export function requestReplayWithBusyRetry(
  client,
  options,
  {
    signal = null,
    isCancelled = () => false,
  } = {},
) {
  return queueReplayRequest(client, async () => {
    for (let attempt = 0; ; attempt += 1) {
      if (signal?.aborted || isCancelled()) return { ok: false, reason: 'cancelled' };
      const result = await requestReplayWithCancellation(client, options, signal);
      if (signal?.aborted || isCancelled()) return { ok: false, reason: 'cancelled' };
      if (result?.reason !== 'busy' || attempt >= REPLAY_BUSY_RETRY_DELAYS_MS.length) {
        return result;
      }
      await new Promise((resolve) => {
        const finish = () => {
          signal?.removeEventListener('abort', onAbort);
          resolve();
        };
        const onAbort = () => {
          clearTimeout(timer);
          finish();
        };
        const timer = setTimeout(finish, REPLAY_BUSY_RETRY_DELAYS_MS[attempt]);
        signal?.addEventListener('abort', onAbort, { once: true });
      });
    }
  }, { signal });
}

function waitForLiveMarketIntelligenceRetry(delayMs, { signal = null } = {}) {
  return new Promise((resolve) => {
    const finish = () => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    };
    const onAbort = () => {
      clearTimeout(timer);
      finish();
    };
    const timer = setTimeout(finish, delayMs);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export async function hydrateLiveMarketIntelligenceHistory({
  client,
  discovery,
  isCancelled = () => false,
  signal = null,
  requestReplay = requestReplayWithBusyRetry,
  waitForRetry = waitForLiveMarketIntelligenceRetry,
  getMarketIntelligenceState = () => useMarketIntelligenceStore.getState(),
  publishPosts = (tradingDate, posts) => (
    useMarketIntelligenceStore.getState().setHistoricalPosts(tradingDate, posts)
  ),
}) {
  const tradingDate = discovery?.tradingDate;
  if (!client || !tradingDate || signal?.aborted || isCancelled()) {
    return { ok: false, reason: 'unavailable' };
  }

  for (let attempt = 0; !signal?.aborted && !isCancelled(); attempt += 1) {
    const current = getMarketIntelligenceState();
    if (
      current.tradingDate === tradingDate
      && current.hasHydratedPosts
      && current.updateSource !== 'replay_v2'
    ) {
      return { ok: true, status: 'live' };
    }
    if (current.historicalTradingDate === tradingDate) {
      return { ok: true, status: 'cached' };
    }

    let response;
    try {
      response = validateReplayPayload(await requestReplay(client, {
        tradingDate,
        sources: ['mi'],
        schemaVersions: [2],
      }, { signal, isCancelled }));
    } catch {
      response = { ok: false, reason: 'request_failed' };
    }
    if (signal?.aborted || isCancelled() || response.reason === 'cancelled') {
      return { ok: false, reason: 'cancelled' };
    }
    const posts = response.datasets?.mi;
    if (
      response.ok
      && response.schemaVersion === 2
      && response.tradingDate === tradingDate
      && response.coverage?.mi
      && Array.isArray(posts)
    ) {
      publishPosts(tradingDate, posts);
      return { ok: true, status: 'requested' };
    }

    await waitForRetry(
      LIVE_MI_HISTORY_RETRY_DELAYS_MS[
        Math.min(attempt, LIVE_MI_HISTORY_RETRY_DELAYS_MS.length - 1)
      ],
      { signal },
    );
  }
  return { ok: false, reason: 'cancelled' };
}

export function cancelReplayHistoryPreload() {
  const run = activeReplayHistoryPreload;
  if (!run) return false;
  run.cancelled = true;
  run.controller.abort();
  activeReplayHistoryPreload = null;
  return true;
}

export function cancelReplayHistoryTopUp() {
  const run = activeReplayHistoryTopUp;
  if (!run) return false;
  activeReplayHistoryTopUp = null;
  run.controller.abort();
  return true;
}

export function startReplayHistoryTopUp(tradingDate, options = {}) {
  cancelReplayHistoryTopUp();
  const controller = new AbortController();
  const run = { controller, promise: null };
  const promise = recoverReplayHistory(tradingDate, {
    ...options,
    signal: controller.signal,
  }).finally(() => {
    if (activeReplayHistoryTopUp === run) activeReplayHistoryTopUp = null;
  });
  run.promise = promise;
  activeReplayHistoryTopUp = run;
  return promise;
}

export async function preloadReplayHistoryDates({
  client,
  dates,
  selectedTradingDate,
  isCancelled = () => false,
  signal = null,
  loadSession = loadReplayDatasetSession,
  persistSession = persistReplayDatasetSession,
  touchActiveDate = touchReplayHistoryDate,
}) {
  const cancelled = () => signal?.aborted || isCancelled();
  let cached = 0;
  let skipped = 0;
  const queuedDates = (dates || [])
    .filter((entry) => (
      entry?.tradingDate
      && !entry.isLatest
      && entry.tradingDate !== selectedTradingDate
    ))
    .slice(0, Math.max(0, REPLAY_DATE_CACHE_CAPACITY - 1));
  if (selectedTradingDate) {
    try {
      await touchActiveDate(selectedTradingDate);
    } catch {
      return { ok: false, reason: 'active_touch_failed', cached, skipped };
    }
  }
  for (const discovery of queuedDates) {
    if (cancelled()) return { ok: false, reason: 'cancelled', cached, skipped };

    const stored = await loadSession(discovery.tradingDate).catch(() => null);
    if (cancelled()) return { ok: false, reason: 'cancelled', cached, skipped };
    if (replayDatasetSessionMatchesCoverage(stored, discovery)) {
      skipped += 1;
      continue;
    }

    let response;
    try {
      response = validateReplayPayload(await requestReplayWithBusyRetry(client, {
        tradingDate: discovery.tradingDate,
        schemaVersions: [2],
      }, {
        signal,
        isCancelled,
      }));
    } catch {
      response = { ok: false, reason: 'request_failed' };
    }
    if (cancelled()) return { ok: false, reason: 'cancelled', cached, skipped };
    if (!response.ok || response.schemaVersion !== 2) {
      return { ok: false, reason: response.reason || 'invalid_response', cached, skipped };
    }
    if (response.tradingDate !== discovery.tradingDate) {
      return { ok: false, reason: 'session_mismatch', cached, skipped };
    }

    const session = {
      ...response,
      requestedRange: null,
      datasetRecordCounts: Object.fromEntries(
        Object.entries(response.datasets).map(([dataset, records]) => [dataset, records.length]),
      ),
    };
    try {
      await persistSession(session);
    } catch {
      return { ok: false, reason: 'persist_failed', cached, skipped };
    }
    cached += 1;
  }
  return { ok: true, cached, skipped };
}

export function startReplayHistoryPreload({
  client,
  dates,
  selectedTradingDate,
  loadSession,
  persistSession,
  touchActiveDate,
}) {
  cancelReplayHistoryPreload();
  const controller = new AbortController();
  const run = { controller, cancelled: false };
  const pendingTopUp = activeReplayHistoryTopUp?.promise || null;
  activeReplayHistoryPreload = run;
  const promise = (async () => {
    if (pendingTopUp) await pendingTopUp.catch(() => undefined);
    if (run.cancelled) return { ok: false, reason: 'cancelled', cached: 0, skipped: 0 };
    return preloadReplayHistoryDates({
      client,
      dates,
      selectedTradingDate,
      ...(loadSession ? { loadSession } : {}),
      ...(persistSession ? { persistSession } : {}),
      ...(touchActiveDate ? { touchActiveDate } : {}),
      signal: controller.signal,
      isCancelled: () => run.cancelled,
    });
  })().finally(() => {
    if (activeReplayHistoryPreload === run) activeReplayHistoryPreload = null;
  });
  return promise;
}

export function shouldPreloadReplayHistory({
  enabled,
  historicalSelectedThisSession,
  selected,
}) {
  return Boolean(selected && (enabled || historicalSelectedThisSession));
}

export function replayRecoveryStatusForSkip(skipped) {
  if (skipped === 'persist_failed') return 'persistence_failed';
  if (skipped === 'replay_exited') return 'replay_exited';
  return 'session_mismatch';
}

function replayProvenanceMatches(left, right) {
  return left?.generatedAt === right?.generatedAt
    && JSON.stringify(left?.session) === JSON.stringify(right?.session);
}

export function retainSelectedReplayDate(dates, config, previousDates = []) {
  const sortedDates = [...dates]
    .sort((left, right) => right.tradingDate.localeCompare(left.tradingDate));
  const selectedDate = config?.replayTradingDate;
  if (
    !config?.replayTradingDateManual
    || !/^\d{4}-\d{2}-\d{2}$/.test(selectedDate || '')
    || sortedDates.some((entry) => entry?.tradingDate === selectedDate)
  ) return sortedDates;
  const retained = previousDates.find((entry) => entry?.tradingDate === selectedDate)
    || config.replayTradingDateDiscovery;
  if (retained?.tradingDate !== selectedDate) return sortedDates;
  return [...sortedDates, { ...retained, isLatest: false }]
    .sort((left, right) => right.tradingDate.localeCompare(left.tradingDate));
}

export function replayDiscoverySessionIsOpen(discovery, now = Date.now()) {
  return Number.isFinite(discovery?.session?.open)
    && Number.isFinite(discovery?.session?.close)
    && now >= discovery.session.open
    && now <= discovery.session.close;
}

export function replayOpenSessionTopUpRange(session, discovery, now = Date.now()) {
  if (!replayDiscoverySessionIsOpen(discovery, now)) return null;
  const cadence = Number(discovery.session.minuteCadenceMs) || 60_000;
  const targetTo = Math.min(
    discovery.session.close,
    Math.floor(now / cadence) * cadence + cadence,
  );
  const coveredEnds = Object.values(session?.coverage || {}).flatMap((entry) => {
    const ranges = entry?.availableRanges || [];
    if (!ranges.length) return [];
    return [Math.max(...ranges.map((range) => Number(range?.to)).filter(Number.isFinite))];
  }).filter(Number.isFinite);
  const coveredTo = coveredEnds.length ? Math.min(...coveredEnds) : discovery.session.open;
  const from = Math.max(
    discovery.session.open,
    coveredTo < targetTo ? coveredTo : targetTo - cadence,
  );
  return targetTo > from ? { from, to: targetTo } : null;
}

export function mergeReplayRequestRanges(...ranges) {
  const valid = ranges.filter((range) => (
    Number.isFinite(range?.from)
    && Number.isFinite(range?.to)
    && range.to > range.from
  ));
  if (!valid.length) return null;
  return {
    from: Math.min(...valid.map((range) => range.from)),
    to: Math.max(...valid.map((range) => range.to)),
  };
}

export function replayHistoryNeedsDiscoverySwitch(playback, config, target) {
  const activeReplayDay = playback?.mode === 'playing' && playback?.replayHistoryActive
    ? (playback.replaySource?.tradingDate || playback.bufferDate)
    : null;
  const stagedReplayDay = playback?.replayDateSelection?.tradingDate
    || playback?.stagedReplayDate
    || null;
  const targetSession = activeReplayDay
    ? playback?.replayDatasetSession
    : (playback?.stagedReplayDatasetSession || playback?.replayDatasetSession);
  const preparedReplayDay = activeReplayDay || stagedReplayDay
    || targetSession?.tradingDate || null;
  return Boolean(target)
    && (
      preparedReplayDay !== target.tradingDate
      || config?.replayTradingDate !== target.tradingDate
      || !replayDatasetSessionMatchesCoverage(targetSession, target)
    );
}

export function scheduleReplayDateMarketOpenRefresh(callback, now = Date.now()) {
  return setTimeout(callback, millisecondsUntilNextMarketOpen(now) + 1_000);
}

export async function switchReplayHistoryDate(
  tradingDate,
  {
    discovery = null,
    manual = true,
    persistSelection = true,
    isCancelled = () => false,
    signal = null,
  } = {},
) {
  cancelReplayHistoryTopUp();
  cancelReplayHistoryPreload();
  const cancelled = () => signal?.aborted || isCancelled();
  if (cancelled()) return { ok: false, reason: 'cancelled' };
  if (!discovery) return { ok: false, reason: 'unavailable' };
  const wasReplaying = usePlaybackStore.getState().mode === 'playing';
  const cachedMetadata = await loadReplayDatasetSession(
    tradingDate,
    { includeRecords: false },
  ).catch(() => null);
  if (cancelled()) return { ok: false, reason: 'cancelled' };
  await usePlaybackStore.getState().selectReplayHistoryDate(tradingDate, { discovery });
  if (cancelled()) {
    usePlaybackStore.getState().discardStagedReplayHistoryDate();
    return { ok: false, reason: 'cancelled' };
  }
  const playback = usePlaybackStore.getState();
  const cached = playback.stagedReplayDatasetCacheCandidate;
  const cacheHasCompatibleProvenance = replayDatasetSessionHasCompatibleProvenance(
    cached,
    discovery,
  );
  const metadataHasCompatibleProvenance = replayDatasetSessionMetadataHasCompatibleProvenance(
    cachedMetadata,
    discovery,
  );
  const openSessionRange = replayOpenSessionTopUpRange(cached || cachedMetadata, discovery);
  const missingRange = replayCoverageMissingRange(cached || cachedMetadata, discovery);
  const requestedRange = cacheHasCompatibleProvenance || metadataHasCompatibleProvenance
    ? mergeReplayRequestRanges(missingRange, openSessionRange)
    : null;
  const preparedCurrentSession = Boolean(
    openSessionRange
    && playback.replayDateSelection?.tradingDate === tradingDate
    && playback.stagedReplayFrames.length > 0
  );
  if (preparedCurrentSession) {
    if (cancelled()) {
      usePlaybackStore.getState().discardStagedReplayHistoryDate();
      return { ok: false, reason: 'cancelled' };
    }
    const selectedHistoryReady = wasReplaying
      ? usePlaybackStore.getState().activateReplayHistoryDate({
        partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
      })
      : true;
    if (!selectedHistoryReady) {
      usePlaybackStore.getState().discardStagedReplayHistoryDate();
      return { ok: false, reason: 'empty' };
    }
    usePlaybackStore.getState().commitStagedReplayHistoryDate();
    if (persistSelection) {
      useDashboardConfig.getState().setReplayTradingDate(tradingDate, { manual, discovery });
    }
    void startReplayHistoryTopUp(tradingDate, {
      requestedRange,
      discovery,
      cachedSession: cached,
    }).catch((error) => {
      console.error('[Replay] current-session top-up failed:', error);
    });
    return {
      ok: true,
      status: 'refreshing',
      recovered: 0,
      coverage: cached?.coverage || cachedMetadata?.coverage || null,
    };
  }
  const result = replayDatasetSessionMatchesCoverage(cached, discovery) && !openSessionRange
    ? { ok: true, status: 'complete', recovered: 0, coverage: cached.coverage }
    : await recoverReplayHistory(tradingDate, {
      requestedRange,
      discovery,
      cachedSession: cached,
      signal,
    });
  if (!result.ok) {
    usePlaybackStore.getState().discardStagedReplayHistoryDate();
    return result;
  }
  if (cancelled()) {
    usePlaybackStore.getState().discardStagedReplayHistoryDate();
    return { ok: false, reason: 'cancelled' };
  }
  const selectedHistoryReady = wasReplaying
    ? usePlaybackStore.getState().activateReplayHistoryDate({
      partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
    })
    : usePlaybackStore.getState().stagedReplayFrames.length > 0;
  if (!selectedHistoryReady) {
    usePlaybackStore.getState().discardStagedReplayHistoryDate();
    return { ok: false, reason: 'empty' };
  }
  if (persistSelection) {
    useDashboardConfig.getState().setReplayTradingDate(tradingDate, { manual, discovery });
  }
  return result;
}

export async function recoverReplayHistory(
  tradingDate = null,
  {
    requestedRange = null,
    discovery = null,
    cachedSession = null,
    signal = null,
  } = {},
) {
  cancelReplayHistoryTopUp();
  cancelReplayHistoryPreload();
  const client = getActiveChainSnapshotClient();
  const localFrames = usePlaybackStore.getState().bufferFrames;
  const localGaps = countReplayCoverageGaps(localFrames);
  if (signal?.aborted) return replayCancellationResult(localGaps);
  if (!client) {
    return { ok: false, status: 'fetch_failed', reason: 'no_connection', remainingGaps: localGaps };
  }

  let replayResult;
  let expectedTradingDate = tradingDate;
  try {
    replayResult = validateReplayPayload(await requestReplayWithBusyRetry(client, {
      tradingDate,
      ...(requestedRange ? { requestedRange } : {}),
    }, { signal }));
    if (signal?.aborted) return replayCancellationResult(localGaps);
    if (
      !tradingDate
      && replayResult.ok
      && replayResult.schemaVersion !== 2
      && replayResult.tradingDate
    ) {
      expectedTradingDate = replayResult.tradingDate;
      try {
        const negotiated = validateReplayPayload(await requestReplayWithCancellation(
          client,
          { tradingDate: replayResult.tradingDate },
          signal,
        ));
        if (signal?.aborted) return replayCancellationResult(localGaps);
        if (negotiated.ok) replayResult = negotiated;
      } catch {
        // Keep the valid V1 result when the server cannot negotiate V2.
      }
    }
  } catch {
    return { ok: false, status: 'fetch_failed', reason: 'request_failed', remainingGaps: localGaps };
  }
  if (!replayResult.ok) {
    if (replayResult.reason === 'unavailable') {
      return { ok: true, status: 'no_history', recovered: 0, remainingGaps: localGaps };
    }
    return {
      ok: false,
      status: replayResult.reason === 'invalid_response' ? 'invalid_response' : 'fetch_failed',
      reason: replayResult.reason,
      remainingGaps: localGaps,
    };
  }
  if (replayResult.schemaVersion === 2) {
    if (expectedTradingDate && replayResult.tradingDate !== expectedTradingDate) {
      return {
        ok: false,
        status: 'session_mismatch',
        reason: 'session_mismatch',
        remainingGaps: localGaps,
      };
    }
    let coverage = null;
    if (requestedRange && discovery) {
      if (!replayProvenanceMatches(replayResult, discovery)) {
        try {
          replayResult = validateReplayPayload(await requestReplayWithCancellation(
            client,
            { tradingDate },
            signal,
          ));
          if (signal?.aborted) return replayCancellationResult(localGaps);
        } catch {
          return {
            ok: false,
            status: 'fetch_failed',
            reason: 'request_failed',
            remainingGaps: localGaps,
          };
        }
        if (!replayResult.ok || replayResult.schemaVersion !== 2) {
          return {
            ok: false,
            status: replayResult?.reason === 'invalid_response' ? 'invalid_response' : 'fetch_failed',
            reason: replayResult?.reason || 'request_failed',
            remainingGaps: localGaps,
          };
        }
        if (replayResult.tradingDate !== tradingDate) {
          return {
            ok: false,
            status: 'session_mismatch',
            reason: 'session_mismatch',
            remainingGaps: localGaps,
          };
        }
      } else {
        coverage = discovery.coverage;
      }
    }
    if (signal?.aborted) return replayCancellationResult(localGaps);
    const hydration = await usePlaybackStore.getState()
      .hydrateReplayDatasetsFromRecovery(replayResult, { coverage, cachedSession, signal });
    if (signal?.aborted) return replayCancellationResult(localGaps);
    const remainingGaps = countReplayCoverageGaps(usePlaybackStore.getState().bufferFrames);
    if (hydration.skipped) {
      return {
        ok: false,
        status: replayRecoveryStatusForSkip(hydration.skipped),
        reason: hydration.skipped,
        recovered: hydration.recovered,
        remainingGaps,
      };
    }
    const coverageComplete = Object.values(replayResult.coverage).every(
      (entry) => entry.status === 'complete' || entry.status === 'notRequested',
    );
    return {
      ok: true,
      status: coverageComplete && remainingGaps === 0 ? 'complete' : 'partial',
      recovered: hydration.recovered,
      remainingGaps,
      coverage: replayResult.coverage,
    };
  }
  if (expectedTradingDate && replayResult.tradingDate !== expectedTradingDate) {
    return { ok: false, status: 'session_mismatch', reason: 'session_mismatch', remainingGaps: localGaps };
  }
  if (!replayResult.frames.length) {
    return { ok: true, status: 'no_history', recovered: 0, remainingGaps: localGaps };
  }

  if (signal?.aborted) return replayCancellationResult(localGaps);
  const hydration = await usePlaybackStore.getState().hydrateReplayFramesFromRecovery(
    replayResult.frames,
    { tradingDate: replayResult.tradingDate, signal },
  );
  if (signal?.aborted) return replayCancellationResult(localGaps);
  if (replayResult.index) {
    useWebSocketDataStore.getState().setHydratedIndexData(replayResult.index);
    usePlaybackStore.getState().refreshReplaySources();
  }
  const remainingGaps = countReplayCoverageGaps(usePlaybackStore.getState().bufferFrames);
  if (hydration.skipped) {
    return {
      ok: false,
      status: replayRecoveryStatusForSkip(hydration.skipped),
      reason: hydration.skipped,
      recovered: hydration.recovered,
      remainingGaps,
    };
  }
  return {
    ok: true,
    status: remainingGaps === 0 ? 'complete' : 'partial',
    recovered: hydration.recovered,
    remainingGaps,
  };
}

// Discards locally-captured/cached chain history and replaces it wholesale
// with a fresh server fetch - the manual recovery path for local corruption
// that predates this session's fixes (a source hydrateDailyCache/live capture
// bug can no longer produce, but cannot retroactively repair once persisted).
// Both halves of a resync, fetched back to back so their trading dates can be
// compared before either is applied.
async function fetchResyncSession() {
  const client = getActiveChainSnapshotClient();
  if (!client) return { chainResult: { ok: false, reason: 'no_connection' }, replayResult: null };

  const chainResult = validateChainSnapshotPayload(await client.requestResync());
  if (!chainResult.ok) return { chainResult, replayResult: null };
  // v2 uses the preceding resync date. Dateless recovery first discovers its
  // date through v1, then negotiates a dated v2 upgrade.
  const replayPayload = await requestReplayWithBusyRetry(client, {
    tradingDate: chainResult.tradingDate,
  });
  const replayResult = replayPayload?.schemaVersion === 2
    ? validateReplayV2Payload(replayPayload)
    : validateReplayFramePayload(replayPayload);
  return { chainResult, replayResult };
}

function resyncSessionsAgree(chainResult, replayResult) {
  return !replayResult?.ok || replayResult.tradingDate === chainResult.tradingDate;
}

function replayChainMinuteCount(frames, tradingDate) {
  return new Set((frames || []).filter((frame) => (
    Number.isFinite(frame?.t) && etDateKey(frame.t) === tradingDate
  )).map((frame) => Math.floor(frame.t / 60_000))).size;
}

export async function forceResyncChainSnapshots(memberCredentials) {
  // The service can roll from the fallback session to the current one between
  // the two requests. Applying that pair would leave prior-session chain
  // snapshots beside current-session replay frames on the same surface, so
  // nothing is written until both payloads name one session. A roll happens at
  // most once per session, so a single retry settles it.
  // ES history repair remains independent because the hydration service owns
  // only SPX history.
  const indexHistoryRequested = requestIndexHistoryRefresh();
  let { chainResult, replayResult } = await fetchResyncSession();
  if (!chainResult.ok) return { ...chainResult, indexHistoryRequested };
  if (!resyncSessionsAgree(chainResult, replayResult)) {
    ({ chainResult, replayResult } = await fetchResyncSession());
    if (!chainResult.ok) return { ...chainResult, indexHistoryRequested };
    if (!resyncSessionsAgree(chainResult, replayResult)) {
      return { ok: false, reason: 'session_rolled', indexHistoryRequested };
    }
  }
  const { tradingDate, snaps } = chainResult;
  const isLatestLiveSession = tradingDate === mostRecentSessionDate();
  const replayChainMinutesBefore = replayChainMinuteCount(
    usePlaybackStore.getState().bufferFrames,
    tradingDate,
  );
  const merged = mergeBackfilledChainSnapshots(snaps, []);
  if (isLatestLiveSession) {
    useOptionsStore.getState().applyAuthoritativeChainSnapshotInit({
      snaps: merged,
      tradingDate,
      fallback: false,
    });
    if (chainResult.index) {
      useWebSocketDataStore.getState().setHydratedIndexData(chainResult.index);
    }
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('chainSnaps_')) localStorage.removeItem(key);
      }
      localStorage.setItem(chainSnapsKey(tradingDate), JSON.stringify(merged));
    } catch {
      // Non-fatal: storage quota or private mode.
    }
  }

  let replayRecovered = 0;
  let replayGaps = countReplayGaps(usePlaybackStore.getState().bufferFrames);
  let replayHydration = replayResult.ok ? 'ok' : replayResult.reason;
  if (replayResult.ok) {
    const hydration = replayResult.schemaVersion === 2
      ? await usePlaybackStore.getState().hydrateReplayDatasetsFromRecovery(replayResult)
      : await usePlaybackStore.getState().hydrateReplayFramesFromRecovery(replayResult.frames, {
        tradingDate: replayResult.tradingDate,
      });
    replayRecovered = hydration.recovered;
    replayGaps = hydration.remainingGaps;
    if (hydration.skipped) replayHydration = hydration.skipped;
  }

  const replayChainMinutesAfter = replayChainMinuteCount(
    usePlaybackStore.getState().bufferFrames,
    tradingDate,
  );
  return {
    ok: true,
    tradingDate,
    replayRecovered,
    replayGaps,
    replayHydration,
    replayChainMinutesBefore,
    replayChainMinutesAfter,
    ...(!isLatestLiveSession ? { liveHydration: 'stale_session' } : {}),
    indexHydration: isLatestLiveSession && chainResult.index
      ? useWebSocketDataStore.getState().indexHydration
      : { status: 'unavailable', reason: 'not_reported', tradingDate: null, coverage: null },
    indexHistoryRequested,
  };
}

function plainObjectShallowEqual(left = {}, right = {}) {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  for (const key of leftKeys) {
    if (!Object.prototype.hasOwnProperty.call(right, key)) return false;
    if (!Object.is(left[key], right[key])) return false;
  }
  return true;
}

export function chainSnapshotsEqual(left = [], right = []) {
  if (left === right) return true;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    const leftSnap = left[index] || {};
    const rightSnap = right[index] || {};
    if (!Object.is(leftSnap.t, rightSnap.t)) return false;
    if (!Object.is(leftSnap.spot ?? null, rightSnap.spot ?? null)) return false;
    if (!Object.is(leftSnap.hydrationFrom ?? null, rightSnap.hydrationFrom ?? null)) return false;
    if (!Object.is(leftSnap.hydrationTo ?? null, rightSnap.hydrationTo ?? null)) return false;
    if (!Object.is(leftSnap.sourceT ?? null, rightSnap.sourceT ?? null)) return false;
    if (!Object.is(Boolean(leftSnap.serviceSnapshotOverlay), Boolean(rightSnap.serviceSnapshotOverlay))) return false;
    if (!Object.is(Boolean(leftSnap.hydrationSeam), Boolean(rightSnap.hydrationSeam))) return false;
    if (!Object.is(Boolean(leftSnap.hydrationBoundaryTail), Boolean(rightSnap.hydrationBoundaryTail))) return false;

    const leftByStrike = leftSnap.byStrike || {};
    const rightByStrike = rightSnap.byStrike || {};
    const leftStrikes = Object.keys(leftByStrike);
    const rightStrikes = Object.keys(rightByStrike);
    if (leftStrikes.length !== rightStrikes.length) return false;
    for (const strike of leftStrikes) {
      if (!Object.prototype.hasOwnProperty.call(rightByStrike, strike)) return false;
      if (!plainObjectShallowEqual(
        leftByStrike[strike] || {},
        rightByStrike[strike] || {}
      )) {
        return false;
      }
    }
  }
  return true;
}

function latestSnapshotEpoch(snaps = []) {
  const last = snaps[snaps.length - 1];
  return Number.isFinite(last?.t) ? last.t : null;
}

function stripHydrationFlagsAndStampSource(snap = {}) {
  const { hydrationSeam, hydrationBoundaryTail, ...rest } = snap || {};
  if (Number.isFinite(rest.t) && !Number.isFinite(rest.sourceT)) {
    rest.sourceT = chainSnapshotMinuteEnd(rest.t);
    rest.serviceSnapshotOverlay = true;
  }
  return rest;
}

function markBrowserHydratedChainSnaps(snaps = []) {
  // windowedSnap retroactively strips any far-out-of-window contamination already
  // baked into a browser-cached snapshot (e.g. from before this guard existed),
  // not just what captureChainSnapshotAt now excludes going forward.
  const cleanSnaps = snaps.map((snap, index) => {
    const hasInteriorSourceSeam = index > 0 && snap?.hydrationSeam === true;
    const cleanSnap = windowedSnap(stripHydrationFlagsAndStampSource(snap));
    return hasInteriorSourceSeam
      ? { ...cleanSnap, hydrationSeam: true }
      : cleanSnap;
  });
  if (!cleanSnaps.length) return cleanSnaps;
  return cleanSnaps.map((snap, index) => (
    index === cleanSnaps.length - 1
      ? { ...snap, hydrationBoundaryTail: true }
      : snap
  ));
}

function pickFreshestChainSnaps(...candidates) {
  const valid = candidates.filter((snaps) => Array.isArray(snaps) && snaps.length > 0);
  if (!valid.length) return null;

  return valid.reduce((best, current) => {
    if (!best) return current;

    const bestT = latestSnapshotEpoch(best);
    const currentT = latestSnapshotEpoch(current);

    if (bestT == null) return current;
    if (currentT == null) return best;
    if (currentT !== bestT) return currentT > bestT ? current : best;
    return current.length > best.length ? current : best;
  }, null);
}

export async function applyReplayDateDiscovery(result, {
  isCancelled,
  getConfig,
  getPlayback,
  setDates,
  switchDate,
}) {
  if (!result?.ok || isCancelled()) return false;
  const config = getConfig();
  const dates = retainSelectedReplayDate(result.dates, config);
  const targetDate = config.replayTradingDateManual
    ? config.replayTradingDate
    : result.latestTradingDate;
  const target = dates.find((entry) => entry.tradingDate === targetDate) || null;
  const requiresActiveSwitch = replayHistoryNeedsDiscoverySwitch(
    getPlayback(),
    config,
    target,
  );
  config.applyLatestReplayTradingDate(result.latestTradingDate, {
    advanceDefault: !requiresActiveSwitch,
  });
  if (!requiresActiveSwitch) {
    setDates(dates);
    return true;
  }
  const switchResult = await switchDate(target.tradingDate, {
    discovery: target,
    replayDates: dates,
    manual: config.replayTradingDateManual,
  }).catch(() => ({ ok: false }));
  if (switchResult?.ok) {
    setDates(dates);
    return true;
  }
  const latestTarget = dates.find((entry) => entry.isLatest) || null;
  if (
    !config.replayTradingDateManual
    || !latestTarget
    || latestTarget.tradingDate === target.tradingDate
  ) return false;
  const fallbackResult = await switchDate(latestTarget.tradingDate, {
    discovery: latestTarget,
    replayDates: dates,
    manual: false,
  }).catch(() => ({ ok: false }));
  if (!fallbackResult?.ok) return false;
  setDates(dates);
  return true;
}

function App() {
  const [loading, setLoading] = useState(true);
  const [memberCredentials, setMemberCredentials] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [replayDates, setReplayDates] = useState([]);
  const [replayDateDiscoveryReady, setReplayDateDiscoveryReady] = useState(
    () => !chainSnapshotWssUrl(),
  );
  const [replayControlReady, setReplayControlReady] = useState(
    () => !chainSnapshotWssUrl(),
  );
  const [historicalReplaySelectedThisSession, setHistoricalReplaySelectedThisSession] = useState(
    false,
  );

  const isDataReady = useOptionsStore((s) => s.isDataReady);
  const playbackMode = usePlaybackStore((s) => s.mode);
  const replayTradingDate = useDashboardConfig((s) => s.replayTradingDate);
  const replayHistoryPreloadEnabled = useDashboardConfig(
    (s) => s.replayHistoryPreloadEnabled,
  );
  const captureChainSnapshot = useOptionsStore((s) => s.captureChainSnapshot);
  const chainSnaps = useOptionsStore((s) => s.chainSnaps);
  const chainSnapsFallback = useOptionsStore((s) => s.chainSnapsFallback);
  const chainSnapshotSource = useOptionsStore((s) => s.chainSnapshotHydration?.source);

  useEffect(() => {
    let cancelled = false;

    async function loadCredentials() {
      try {
        const credentials = await resolveMemberCredentials();
        if (cancelled || !credentials) return;

        setMemberCredentials(credentials);
      } catch (error) {
        console.error('[AUTH] Authentication error:', error);

        if (AUTH_MODE === 'memberstack') {
          window.location.href = REDIRECTS.login;
          return;
        }

        if (!cancelled) {
          setAuthError(error.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCredentials();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!memberCredentials?.memberId || !memberCredentials?.status) return;
    const stop = startStream({
      useMock: false,
      memberCredentials,
    });

    return stop;
  }, [memberCredentials]);

  useEffect(() => {
    try {
      const bridgedSnaps = readWindowNameChainSnaps();
      const currentTradingDate = mostRecentSessionDate();
      const currentKey = chainSnapsKey(currentTradingDate);
      const cacheKeys = Object.keys(localStorage)
        .filter((key) => key.startsWith('chainSnaps_'))
        .sort();
      const localSnaps = readChainSnapsCache(currentKey);
      const currentSnaps = pickFreshestChainSnaps(
        snapshotsForTradingDate(localSnaps, currentTradingDate),
        snapshotsForTradingDate(bridgedSnaps, currentTradingDate)
      );
      const snaps = currentSnaps;

      if (snaps) {
        useOptionsStore.getState().hydrateChainSnapshotCache({
          snaps: markBrowserHydratedChainSnaps(snaps),
          source: 'current-browser-cache',
          tradingDate: currentTradingDate,
          fallback: false,
        });
        useOptionsStore.getState().setDataReady(true);
      }

      for (const key of cacheKeys) {
        if (key !== currentKey) localStorage.removeItem(key);
      }
    } catch {
      // Non-fatal: private mode or a corrupt local cache should not break boot.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let rolloverId = null;
    const enforceLatestSession = () => {
      if (cancelled || usePlaybackStore.getState().mode === 'playing') return;
      useOptionsStore.getState().beginChainSnapshotHydration({
        tradingDate: mostRecentSessionDate(),
        resetMismatched: true,
      });
    };
    const scheduleRollover = () => {
      clearTimeout(rolloverId);
      rolloverId = setTimeout(() => {
        enforceLatestSession();
        scheduleRollover();
      }, millisecondsUntilNextMarketOpen() + 1_000);
    };

    enforceLatestSession();
    scheduleRollover();
    const unsubscribePlayback = usePlaybackStore.subscribe((state, previousState) => {
      if (previousState.mode === 'playing' && state.mode !== 'playing') {
        queueMicrotask(enforceLatestSession);
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(rolloverId);
      unsubscribePlayback();
    };
  }, []);

  useEffect(() => {
    hydrateDailyCache();
  }, []);

  useEffect(() => {
    if (!chainSnapshotWssUrl()) {
      useOptionsStore.getState().degradeChainSnapshotHydration();
      setReplayDateDiscoveryReady(true);
      setReplayControlReady(true);
      return undefined;
    }

    if (!memberCredentials) {
      if (!authError) return undefined;
      useOptionsStore.getState().degradeChainSnapshotHydration({
        source: 'credentials-unavailable',
      });
      setReplayDateDiscoveryReady(true);
      setReplayControlReady(true);
      return undefined;
    }

    if (!hasValidChainSnapshotCredentials(memberCredentials)) {
      useOptionsStore.getState().degradeChainSnapshotHydration({
        source: 'service-unauthorized',
      });
      setReplayDateDiscoveryReady(true);
      setReplayControlReady(true);
      return undefined;
    }

    let cancelled = false;
    setReplayDateDiscoveryReady(false);
    setReplayControlReady(false);
    const replayDateDiscoveryFallbackId = scheduleReplayDiscoveryFallback(
      setReplayControlReady,
      () => cancelled,
    );

    let deferred = null;
    let acceptedInit = false;

    // Credential refreshes must not replace the chain state owned by replay.
    if (usePlaybackStore.getState().mode !== 'playing') {
      useOptionsStore.getState().beginChainSnapshotHydration({
        tradingDate: mostRecentSessionDate(),
        resetMismatched: true,
      });
    }

    const persistSnapshots = (tradingDate, snaps) => {
      try {
        const targetKey = chainSnapsKey(tradingDate);
        for (const key of Object.keys(localStorage)) {
          if (key.startsWith('chainSnaps_') && key !== targetKey) localStorage.removeItem(key);
        }
        localStorage.setItem(targetKey, JSON.stringify(snaps));
      } catch {
        // Non-fatal: storage quota or private mode.
      }
    };

    const applyValidatedInit = (result) => {
      const { tradingDate: responseTradingDate, snaps: apiSnaps } = result;
      const currentState = useOptionsStore.getState();
      const currentTradingDate = mostRecentSessionDate();
      if (responseTradingDate !== currentTradingDate) return;
      acceptedInit = true;
      if (!apiSnaps.length) {
        currentState.degradeChainSnapshotHydration({
          source: 'authoritative-init-empty',
        });
        if (result.index) {
          useWebSocketDataStore.getState().setHydratedIndexData(result.index);
        }
        return;
      }
      const current = snapshotsForTradingDate(currentState.chainSnaps, responseTradingDate);
      const apiTimestamps = new Set(apiSnaps.map((snapshot) => Number(snapshot.t)));
      // A full INIT corrects matching cached minutes. Preserve local minutes
      // omitted by a partial service response so reconnect does not create gaps.
      const nonOverlappingCurrent = current.filter(
        (snapshot) => !apiTimestamps.has(Number(snapshot.t))
      );
      const authoritative = mergeBackfilledChainSnapshots(
        apiSnaps,
        nonOverlappingCurrent,
        { markSourceTransitions: true },
      );
      currentState.applyAuthoritativeChainSnapshotInit({
        snaps: authoritative,
        tradingDate: responseTradingDate,
        fallback: false,
      });
      persistSnapshots(responseTradingDate, authoritative);
      if (result.index) {
        useWebSocketDataStore.getState().setHydratedIndexData(result.index);
      }
    };

    const applyValidatedUpdate = (result) => {
      const state = useOptionsStore.getState();
      const { tradingDate: responseTradingDate, snaps: apiSnaps } = result;
      if (responseTradingDate !== mostRecentSessionDate() || !apiSnaps.length) return;
      const current = snapshotsForTradingDate(state.chainSnaps, responseTradingDate);
      const merged = mergeBackfilledChainSnapshots(apiSnaps, current);
      if (
        !chainSnapshotsEqual(state.chainSnaps, merged)
        || state.chainSnapsFallback
        || state.chainSnapshotHydration.lifecycle !== 'ready'
      ) {
        state.applyAuthoritativeChainSnapshotUpdate({
          snaps: merged,
          tradingDate: responseTradingDate,
          complete: acceptedInit,
        });
        persistSnapshots(responseTradingDate, merged);
      }
      if (result.index) {
        useWebSocketDataStore.getState().setHydratedIndexData(result.index);
      }
    };

    // While the buffer is being reviewed, chainSnaps IS the cutoff series that
    // applyCutoff reads, so writing the live day into it moves the Bookmap off
    // the playhead. The local sampler already refuses (captureChainSnapshot
    // returns on status === 'replay') and live option rows queue in
    // pendingLiveUpdates; the socket needs the same gate. REST never did,
    // because a one-shot fetch could not land mid-replay the way a 5s UPDATE,
    // a reconnect INIT, or a rollover INIT can.
    const isReplaying = () => usePlaybackStore.getState().mode === 'playing';

    // Coalesced by minute rather than queued per message: a long review would
    // otherwise accumulate one entry per 5s tick, and only the newest version
    // of any given minute matters.
    const deferPayload = ({ kind, tradingDate, snaps }) => {
      if (tradingDate !== mostRecentSessionDate()) return;
      if (!deferred || deferred.tradingDate !== tradingDate) {
        deferred = { kind, tradingDate, byTimestamp: new Map() };
      } else if (kind === 'init') {
        deferred.kind = 'init';
      }
      for (const snapshot of snaps) deferred.byTimestamp.set(snapshot.t, snapshot);
    };

    const flushDeferred = () => {
      if (cancelled || !deferred) return;
      const { kind, tradingDate, byTimestamp } = deferred;
      deferred = null;
      const result = {
        tradingDate,
        snaps: [...byTimestamp.values()].sort((left, right) => left.t - right.t),
        index: null,
      };
      if (kind === 'init') applyValidatedInit(result);
      else applyValidatedUpdate(result);
    };

    const applyPayload = (kind, payload) => {
      if (cancelled) return;
      const result = validateChainSnapshotPayload(payload, { allowEmpty: kind === 'init' });
      if (!result.ok) return;
      if (isReplaying()) {
        if (result.index) {
          useWebSocketDataStore.getState().setHydratedIndexData(result.index);
          usePlaybackStore.getState().refreshReplaySources();
        }
        deferPayload({ kind, ...result });
        return;
      }
      if (kind === 'init') applyValidatedInit(result);
      else applyValidatedUpdate(result);
    };

    const unsubscribePlayback = usePlaybackStore.subscribe((state, previousState) => {
      if (previousState.mode !== 'playing' || state.mode === 'playing') return;
      // exitPlayback sets mode inside the same set() that precedes
      // drainPendingLiveUpdates, and that restores the whole options slice
      // captured at play start -- chainSnaps included. Flushing synchronously
      // here would be silently overwritten a few statements later.
      queueMicrotask(flushDeferred);
    });

    // The socket's own reconnect-with-backoff replaces the REST retry ladder,
    // the market-open wake, and the visibility repair-on-return. A dropped
    // connection reconnects and reconnecting delivers a full INIT, so the three
    // mechanisms that existed to re-poll a request/response endpoint have
    // nothing left to do.
    let client = null;
    let authenticated = false;
    let replayDateRequestInFlight = false;
    let replayDateTradingDay = null;
    let replayDateRetryId = null;
    let replayDateRetryAttempt = 0;
    let marketOpenRefreshId = null;
    const liveMiHistoryController = new AbortController();
    let liveMiHistoryTradingDate = null;
    let liveMiHistoryPromise = null;
    const startLiveMiHistoryHydration = (discovery) => {
      const tradingDate = discovery?.tradingDate || null;
      if (
        liveMiHistoryPromise
        && liveMiHistoryTradingDate === tradingDate
      ) return liveMiHistoryPromise;

      liveMiHistoryTradingDate = tradingDate;
      const run = hydrateLiveMarketIntelligenceHistory({
        client,
        discovery,
        signal: liveMiHistoryController.signal,
        isCancelled: () => (
          cancelled
          || liveMiHistoryTradingDate !== tradingDate
        ),
      });
      liveMiHistoryPromise = run;
      const clearRun = () => {
        if (liveMiHistoryPromise === run) liveMiHistoryPromise = null;
      };
      void run.then(clearRun, clearRun);
      return run;
    };
    const applyReplayDates = (result) => applyReplayDateDiscovery(result, {
      isCancelled: () => cancelled,
      getConfig: () => useDashboardConfig.getState(),
      getPlayback: () => usePlaybackStore.getState(),
      setDates: setReplayDates,
      switchDate: switchReplayHistoryDate,
    });
    const refreshReplayDates = ({ force = false } = {}) => {
      const currentTradingDay = etDateKey(Date.now());
      if (!authenticated || !client || replayDateRequestInFlight
        || (!force && replayDateTradingDay === currentTradingDay)) return;
      clearTimeout(replayDateRetryId);
      replayDateRetryId = null;
      replayDateRequestInFlight = true;
      let discovered = false;
      void client?.requestReplayDates()
        .then(async (result) => {
          discovered = Boolean(
            result?.ok
            && !result?.stale
            && await applyReplayDates(result)
          );
          if (discovered) {
            const latestDiscovery = result.dates?.find((entry) => (
              entry?.tradingDate === result.latestTradingDate
            ));
            void startLiveMiHistoryHydration(latestDiscovery);
          }
        })
        .finally(() => {
          replayDateRequestInFlight = false;
          if (!cancelled) {
            if (discovered) {
              replayDateTradingDay = currentTradingDay;
              replayDateRetryAttempt = 0;
              setReplayDateDiscoveryReady(true);
              setReplayControlReady(true);
            } else if (authenticated) {
              replayDateRetryId = scheduleReplayDiscoveryRetry(
                refreshReplayDates,
                {
                  attempt: replayDateRetryAttempt,
                  isCancelled: () => cancelled || !authenticated,
                },
              );
              replayDateRetryAttempt += 1;
            }
          }
        });
    };
    const scheduleMarketOpenRefresh = () => {
      clearTimeout(marketOpenRefreshId);
      const runRefresh = () => {
        if (replayDateRequestInFlight) {
          marketOpenRefreshId = setTimeout(runRefresh, 1_000);
          return;
        }
        refreshReplayDates({ force: true });
        scheduleMarketOpenRefresh();
      };
      marketOpenRefreshId = scheduleReplayDateMarketOpenRefresh(runRefresh);
    };
    client = startChainSnapshotClient({
      memberCredentials,
      onInit: (payload) => applyPayload('init', payload),
      onUpdate: (payload) => applyPayload('update', payload),
      onIndexUpdate: (payload) => {
        if (cancelled) return;
        const index = validateHydratedIndexPayload(payload, payload?.tradingDate);
        useWebSocketDataStore.getState().setHydratedIndexData(index, {
          historyUpdate: 'incremental',
        });
        usePlaybackStore.getState().refreshReplaySources();
      },
      onStatus: (value) => {
        if (value === 'authenticated') {
          authenticated = true;
          replayDateRetryAttempt = 0;
          refreshReplayDates({ force: true });
          scheduleMarketOpenRefresh();
          return;
        }
        if (value === 'unauthorized' || value === 'disconnected') {
          useOptionsStore.getState().degradeChainSnapshotHydration({
            source: value === 'unauthorized' ? 'service-unauthorized' : 'service-disconnected',
          });
          authenticated = false;
          clearTimeout(replayDateRetryId);
          replayDateRetryId = null;
          replayDateRetryAttempt = 0;
          if (!cancelled) {
            setReplayDateDiscoveryReady(true);
            setReplayControlReady(true);
          }
        }
      },
    });
    if (authenticated) refreshReplayDates({ force: true });

    const rolloverId = setInterval(() => refreshReplayDates(), 60_000);

    return () => {
      cancelled = true;
      unsubscribePlayback();
      clearInterval(rolloverId);
      clearTimeout(replayDateRetryId);
      clearTimeout(marketOpenRefreshId);
      clearTimeout(replayDateDiscoveryFallbackId);
      liveMiHistoryTradingDate = null;
      liveMiHistoryController.abort();
      client.stop();
    };
  }, [memberCredentials, authError]);

  useEffect(() => {
    if (
      !chainSnaps.length
      || chainSnapsFallback
      || playbackMode === 'playing'
      || chainSnapshotSource === 'replay'
      || chainSnapsTradingDate(chainSnaps) !== mostRecentSessionDate()
    ) return;

    try {
      const targetKey = chainSnapsKey(chainSnapsTradingDate(chainSnaps));
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('chainSnaps_') && key !== targetKey) localStorage.removeItem(key);
      }
      localStorage.setItem(
        targetKey,
        JSON.stringify(chainSnaps)
      );
    } catch {
      // Non-fatal: storage quota or private mode.
    }

    if (process.env.NODE_ENV === 'development') {
      writeWindowNameChainSnaps(chainSnaps);
    }
  }, [chainSnaps, chainSnapsFallback, chainSnapshotSource, playbackMode]);

  useEffect(() => {
    const selected = replayDates.find(
      (entry) => entry.tradingDate === replayTradingDate,
    );
    if (!shouldPreloadReplayHistory({
      enabled: replayHistoryPreloadEnabled,
      historicalSelectedThisSession: historicalReplaySelectedThisSession,
      selected,
    })) {
      cancelReplayHistoryPreload();
      return undefined;
    }
    const client = getActiveChainSnapshotClient();
    if (!client) return undefined;
    void startReplayHistoryPreload({
      client,
      dates: replayDates,
      selectedTradingDate: replayTradingDate,
    }).then((result) => {
      if (!result.ok && result.reason !== 'cancelled') {
        console.debug('[Replay] background preload stopped:', result.reason);
      }
    });
    return cancelReplayHistoryPreload;
  }, [
    replayDates,
    replayTradingDate,
    replayHistoryPreloadEnabled,
    historicalReplaySelectedThisSession,
  ]);

  useEffect(() => {
    const tick = () => {
      captureChainSnapshot();
      if (isMarketOpen() && hasCurrentTradingDayLiveData()) {
        persistDailyCache();
      }
    };

    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [captureChainSnapshot]);

  useEffect(() => {
    if (shouldLockDocumentScroll(isDataReady, playbackMode)) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isDataReady, playbackMode]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (authError) {
    return <div role="alert">Auth configuration error: {authError}</div>;
  }

  return (
    <>
      <div className="App">
        {memberCredentials && (
          <>
            <ZeroDteWssClient />
            <ZeroDteGreeksWebsocketClient memberCredentials={memberCredentials} />
            <MarketIntelligenceWebsocketClient memberCredentials={memberCredentials} />
            <DashboardLayout
              memberCredentials={memberCredentials}
              onRecoverReplayHistory={recoverReplayHistory}
              replayDates={replayDates}
              replayDateDiscoveryReady={replayDateDiscoveryReady}
              replayControlReady={replayControlReady}
              onReplayDateSelect={async (tradingDate, {
                manual = true,
                persistSelection = true,
                isCancelled = () => false,
                signal = null,
              } = {}) => {
                const discovery = replayDates.find(
                  (entry) => entry.tradingDate === tradingDate,
                ) || null;
                const result = await switchReplayHistoryDate(tradingDate, {
                  discovery,
                  replayDates,
                  manual,
                  persistSelection,
                  isCancelled,
                  signal,
                });
                if (result?.ok && manual && discovery && !discovery.isLatest) {
                  setHistoricalReplaySelectedThisSession(true);
                }
                return result;
              }}
            />
          </>
        )}
      </div>
    </>
  );
}

export default App;
